import React, { useRef, useState } from "react";
import { FieldError, FieldValues, Path, UseFormRegister } from "react-hook-form";
import { mdiClose, mdiEye, mdiEyeOff, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { getClassByVariant } from "helpers/getClassByVariant";
import styles from "./style.module.scss";

export interface IProps<T extends FieldValues>
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  name?: Path<T>;
  register?: UseFormRegister<T>;
  label?: string | React.ReactNode;
  className?: string;
  prefixNode?: React.ReactNode;
  prefixUnit?: string;
  suffixUnitClass?: string;
  prefixUnitClass?: string;
  suffixUnit?: string;
  inputClass?: string;
  labelClass?: string;
  isLoading?: boolean;
  disabled?: boolean;
  error?: FieldError;
  warning?: string;
  isHideError?: boolean;
  isPasswordIcon?: boolean;
  isSingleNumber?: boolean;
  isShowClose?: boolean;
  onClear?: VoidFunction;
  color?: "primary" | "secondary" | string;
  required?: boolean;
}

export default function Input<T extends FieldValues>({
  name,
  register,
  label,
  className,
  prefixNode,
  prefixUnit,
  prefixUnitClass,
  suffixUnit,
  suffixUnitClass,
  inputClass,
  labelClass,
  isLoading,
  disabled,
  error,
  warning,
  isHideError = false,
  isPasswordIcon = false,
  isSingleNumber = false,
  isShowClose = false,
  onClear,
  color = "primary",
  autoComplete = "off",
  required = false,
  ...props
}: Readonly<IProps<T>>) {
  const [showPassword, setShowPassword] = useState<boolean>(!isPasswordIcon);
  const [value, setValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (isSingleNumber && newValue.length > 1) {
      newValue = newValue.slice(0, 1);
    }
    setValue(newValue);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const getErrorMsg = () => {
    switch (error?.type) {
      case "email":
        return "This field value must be a valid email";

      case "required":
        return "This field is required";

      default:
        return null;
    }
  };

  return (
    <div className={clsx(styles.smartInputContainer, className)}>
      {label && (
        <label
          htmlFor={props.id}
          className={clsx(styles.smartInputLabel, labelClass, getClassByVariant(color)?.label)}>
          {required ? "* " : ""}
          {label}
        </label>
      )}
      <div className={styles.smartInputWrapper} onClick={() => inputRef.current?.focus()}>
        {prefixUnit && (
          <div className={clsx(styles.smartInputPrefixUnit, prefixUnitClass)}>
            <p dangerouslySetInnerHTML={{ __html: prefixUnit }} />
          </div>
        )}
        {prefixNode}
        <input
          type={isPasswordIcon ? (showPassword ? "text" : "password") : props.type}
          ref={inputRef}
          className={clsx(
            styles.smartInput,
            inputClass,
            getClassByVariant(color)?.input,
            disabled && "cursor-not-allowed !bg-inputDark",
            !!error && "!border-b-error",
            !!prefixUnit && "!rounded-s-none !pl-2.5",
            !!prefixNode && "!pl-12",
            !!suffixUnit && "!rounded-e-none !pr-2.5",
            !!isPasswordIcon && "!pr-12",
            { "!pr-10": isShowClose }
          )}
          autoComplete={autoComplete}
          disabled={disabled}
          {...(isSingleNumber && { value: value })}
          {...(isSingleNumber && { onInput: handleChange })}
          {...(register && name && register(name))}
          {...(props.type === "number" && { onWheel: (e: any) => e.target.blur() })}
          {...(props.type === "number" && { onFocus: (e: any) => e.target.select() })}
          {...props}
        />
        {isPasswordIcon && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className={styles.smartInputPasswordIcon}>
            {showPassword ? (
              <Icon path={mdiEyeOff} className="size-5" />
            ) : (
              <Icon path={mdiEye} className="size-5" />
            )}
          </button>
        )}
        {suffixUnit && (
          <div className={clsx(styles.smartInputSuffixUnit, suffixUnitClass)}>
            <span>{suffixUnit}</span>
          </div>
        )}
        {isLoading && <Icon spin path={mdiLoading} className={styles.smartInputLoader} />}
        {isShowClose && props.value && !isLoading && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center p-3 touch-manipulation"
            onClick={(e) => {
              e.stopPropagation();
              if (inputRef.current) {
                inputRef.current.value = "";
                props.onChange?.({
                  target: inputRef.current
                } as unknown as React.ChangeEvent<HTMLInputElement>);
              }
              onClear && onClear();
            }}>
            <Icon path={mdiClose} className="size-6" />
          </button>
        )}
      </div>
      {error && !isHideError && (
        <span className={styles.smartInputError}>{error.message ?? getErrorMsg()}</span>
      )}
      {warning && <span className={styles.smartInputWarning}>{warning}</span>}
    </div>
  );
}
